import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const ALL_COLUMNS = [
  { key: 'companyName', label: 'Company Name', sortable: true },
  { key: 'contact', label: 'Contact', sortable: false },
  { key: 'activeProjects', label: 'Active Projects', sortable: true },
  { key: 'totalValue', label: 'Total Contract Value', sortable: true },
  { key: 'bidConversionRate', label: 'Conversion Rate', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false },
];

const STATUS_COLORS = {
  'In Progress': 'bg-blue-100 text-blue-700',
  'Planning': 'bg-yellow-100 text-yellow-700',
  'Completed': 'bg-green-100 text-green-700',
  'On Hold': 'bg-orange-100 text-orange-700',
  'Cancelled': 'bg-red-100 text-red-700',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const CustomerListTable = ({ customers, onViewDetails, onEdit, onDelete, sortConfig, onSort, visibleColumns, selectedIds, onSelectRow, onSelectAll, allSelected, someSelected }) => {
  const navigate = useNavigate();
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [projectsMap, setProjectsMap] = useState({});
  const [loadingIds, setLoadingIds] = useState(new Set());

  const activeVisibleColumns = visibleColumns
    ? ALL_COLUMNS?.filter(c => visibleColumns?.includes(c?.key))
    : ALL_COLUMNS;

  const colSpan = activeVisibleColumns?.length + 1; // +1 for checkbox

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const fetchProjectsForCustomer = useCallback(async (customer) => {
    const id = customer?.id;
    if (projectsMap?.[id] !== undefined) return;
    setLoadingIds(prev => new Set(prev)?.add(id));
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.select('id, project_name, status, total_budget, start_date, end_date')
        ?.ilike('customer_name', customer?.companyName)
        ?.order('created_at', { ascending: false });
      setProjectsMap(prev => ({ ...prev, [id]: error ? [] : (data || []) }));
    } catch {
      setProjectsMap(prev => ({ ...prev, [id]: [] }));
    } finally {
      setLoadingIds(prev => { const s = new Set(prev); s?.delete(id); return s; });
    }
  }, [projectsMap]);

  const handleToggleExpand = (e, customer) => {
    e?.stopPropagation();
    const id = customer?.id;
    const next = new Set(expandedIds);
    if (next?.has(id)) {
      next?.delete(id);
    } else {
      next?.add(id);
      fetchProjectsForCustomer(customer);
    }
    setExpandedIds(next);
  };

  const handleProjectClick = (e, projectId) => {
    e?.stopPropagation();
    navigate(`/project-detail-management/${projectId}`);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="px-4 py-3 md:py-4 w-10">
              <input
                type="checkbox"
                checked={allSelected || false}
                ref={el => { if (el) el.indeterminate = someSelected || false; }}
                onChange={onSelectAll}
                className="rounded border-border text-accent focus:ring-accent cursor-pointer"
                title={allSelected ? 'Deselect all' : 'Select all'}
              />
            </th>
            {/* Expand column */}
            <th className="w-8 px-2 py-3 md:py-4" />
            {activeVisibleColumns?.map(col => (
              <th key={col?.key} className="text-left px-4 md:px-6 py-3 md:py-4">
                {col?.sortable ? (
                  <button
                    onClick={() => onSort(col?.key)}
                    className="flex items-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-accent transition-colors"
                  >
                    {col?.label}
                    <Icon name={getSortIcon(col?.key)} size={16} />
                  </button>
                ) : (
                  <span className="text-xs md:text-sm font-semibold text-foreground">{col?.label}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {customers?.map((customer) => {
            const isExpanded = expandedIds?.has(customer?.id);
            const isLoading = loadingIds?.has(customer?.id);
            const projects = projectsMap?.[customer?.id] || [];

            return (
              <React.Fragment key={customer?.id}>
                <tr
                  className={`hover:bg-muted/30 transition-colors cursor-pointer ${
                    selectedIds?.has(customer?.id) ? 'bg-accent/5' : ''
                  }`}
                  onClick={() => onViewDetails(customer)}
                >
                  <td className="px-4 py-3 md:py-4 w-10" onClick={e => { e?.stopPropagation(); onSelectRow?.(customer?.id); }}>
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(customer?.id) || false}
                      onChange={() => {}}
                      className="rounded border-border text-accent focus:ring-accent cursor-pointer"
                    />
                  </td>
                  {/* Expand toggle */}
                  <td className="w-8 px-2 py-3 md:py-4">
                    <button
                      onClick={(e) => handleToggleExpand(e, customer)}
                      className="flex items-center justify-center w-6 h-6 rounded hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                      title={isExpanded ? 'Collapse projects' : 'Expand projects'}
                    >
                      <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={14} />
                    </button>
                  </td>
                  {activeVisibleColumns?.map(col => (
                    <td key={col?.key} className="px-4 md:px-6 py-3 md:py-4">
                      {col?.key === 'companyName' && (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="Building2" size={20} className="text-accent" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm md:text-base font-medium text-foreground truncate capitalize">{customer?.companyName}</p>
                            <p className="text-xs md:text-sm text-muted-foreground truncate capitalize">{customer?.contactPerson}</p>
                          </div>
                        </div>
                      )}
                      {col?.key === 'contact' && (
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate capitalize">{customer?.contactPerson}</p>
                          <p className="text-xs text-muted-foreground truncate">{customer?.email}</p>
                          <p className="text-xs text-muted-foreground">{customer?.phone}</p>
                        </div>
                      )}
                      {col?.key === 'activeProjects' && (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent/10 text-accent text-sm font-semibold">
                            {customer?.activeProjects}
                          </span>
                          <span className="text-xs text-muted-foreground hidden lg:inline">projects</span>
                        </div>
                      )}
                      {col?.key === 'totalValue' && (
                        <p className="text-sm md:text-base font-semibold text-foreground">{formatCurrency(customer?.totalValue)}</p>
                      )}
                      {col?.key === 'bidConversionRate' && (
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-accent rounded-full"
                              style={{ width: `${Math.min(customer?.bidConversionRate || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm text-foreground">{customer?.bidConversionRate || 0}%</span>
                        </div>
                      )}
                      {col?.key === 'actions' && (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e?.stopPropagation(); onEdit(customer); }}
                            className="hover:bg-accent/10 hover:text-accent"
                          >
                            <Icon name="Pencil" size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e?.stopPropagation(); onDelete(customer); }}
                            className="hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Icon name="Trash2" size={16} />
                          </Button>
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
                {/* Expanded project rows */}
                {isExpanded && (
                  <tr>
                    <td colSpan={colSpan + 1} className="p-0 bg-muted/20">
                      <div className="px-6 py-3">
                        {isLoading ? (
                          <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                            <Icon name="Loader2" size={16} className="animate-spin" />
                            Loading projects…
                          </div>
                        ) : projects?.length === 0 ? (
                          <p className="py-3 text-sm text-muted-foreground italic">No projects found for this customer.</p>
                        ) : (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                <th className="text-left py-2 pr-6">Project Name</th>
                                <th className="text-left py-2 pr-6">Status</th>
                                <th className="text-left py-2 pr-6">Budget</th>
                                <th className="text-left py-2 pr-6">Start Date</th>
                                <th className="text-left py-2">End Date</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50">
                              {projects?.map(project => (
                                <tr
                                  key={project?.id}
                                  onClick={(e) => handleProjectClick(e, project?.id)}
                                  className="cursor-pointer hover:bg-accent/10 transition-colors rounded"
                                >
                                  <td className="py-2 pr-6">
                                    <div className="flex items-center gap-2">
                                      <Icon name="FolderOpen" size={14} className="text-accent flex-shrink-0" />
                                      <span className="font-medium text-foreground hover:text-accent transition-colors">
                                        {project?.project_name}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-2 pr-6">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      STATUS_COLORS?.[project?.status] || 'bg-muted text-muted-foreground'
                                    }`}>
                                      {project?.status || '—'}
                                    </span>
                                  </td>
                                  <td className="py-2 pr-6 text-foreground">{formatCurrency(project?.total_budget)}</td>
                                  <td className="py-2 pr-6 text-muted-foreground">{formatDate(project?.start_date)}</td>
                                  <td className="py-2 text-muted-foreground">{formatDate(project?.end_date)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CustomerListTable;